import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { UserActivityTracker, UserActivityTrackerResponse } from '@zayan-erp/administration';
import { RealtimeConnection, RealtimeEnvelope } from '@zayan-erp/realtime';
import { defer, filter, from, map, Observable, ReplaySubject, Subject, take } from 'rxjs';
import { UserActivityCommands, UserActivityEvents } from '../models/user-activity-events';

@Injectable({
  providedIn: 'root'
})
export class SignalRActivityTrackerService {
  readonly #realtime = inject(RealtimeConnection);

  /**
   * Replays the last snapshot so a screen opened after the hub sent it still renders the live
   * sessions instead of waiting for the next broadcast.
   */
  public receiveActivityTrackerData$ = new ReplaySubject<UserActivityTrackerResponse[]>(1);
  public broadcastUserActivity$ = new Subject<UserActivityTrackerResponse>();
  public logoutUser$ = new Subject<UserActivityTrackerResponse>();

  #currentActivity: UserActivityTracker | null = null;

  readonly #connected$ = toObservable(this.#realtime.isConnected).pipe(
    filter(Boolean),
    map(() => undefined as void)
  );

  constructor() {
    this.#realtime.envelopes$.pipe(takeUntilDestroyed()).subscribe(envelope => this.#onEnvelope(envelope));

    // Every connect, not just the first: a reconnected session starts with no page server-side.
    this.#connected$.pipe(takeUntilDestroyed()).subscribe(() => this.#push());
  }

  startConnection(): Observable<void> {
    return this.#connected$.pipe(take(1));
  }

  pushActivityTrackerData(activityData: UserActivityTracker): void {
    this.#currentActivity = activityData;
    this.#push();
  }

  forceLogoutUser(connectionId: string, userId: string): Observable<void> {
    return defer(() =>
      from(this.#realtime.invoke<void>('Send', UserActivityCommands.forceLogout, { connectionId, userId }))
    );
  }

  #onEnvelope(envelope: RealtimeEnvelope): void {
    switch (envelope.type) {
      case UserActivityEvents.snapshot:
        return this.receiveActivityTrackerData$.next((envelope.payload ?? []) as UserActivityTrackerResponse[]);
      case UserActivityEvents.updated:
        return this.broadcastUserActivity$.next(envelope.payload as UserActivityTrackerResponse);
      case UserActivityEvents.loggedOut:
        return this.logoutUser$.next(envelope.payload as UserActivityTrackerResponse);
    }
  }

  #push(): void {
    if (!this.#currentActivity || !this.#realtime.isConnected()) return;

    this.#realtime
      .invoke('Send', UserActivityCommands.push, this.#currentActivity)
      .catch((error: unknown) => console.warn('Realtime: user activity push failed.', error));
  }
}
